import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  IconButton,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  DirectionsBike,
  Event,
  Person
} from '@mui/icons-material';
import { api } from '../../utils/api';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isWithinInterval, startOfWeek, endOfWeek, addMonths, subMonths } from 'date-fns';
import { it } from 'date-fns/locale';

const RentalCalendar = () => {
  const [rentals, setRentals] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('all');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rentalsRes, vehiclesRes] = await Promise.all([
        api.get('/rentals'),
        api.get('/rental-vehicles')
      ]);
      
      setRentals(rentalsRes.data);
      setVehicles(vehiclesRes.data);
    } catch (err) {
      console.error('Error fetching calendar data:', err);
      setError('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const getRentalsForDate = (date) => {
    return rentals.filter(rental => {
      if (selectedVehicle !== 'all' && rental.vehicleId !== parseInt(selectedVehicle)) {
        return false;
      }
      
      if (rental.status === 'completed' || rental.status === 'cancelled') {
        return false;
      }
      
      const start = new Date(rental.startDate);
      const end = new Date(rental.endDate);
      
      return isWithinInterval(date, { start, end }) || isSameDay(date, start) || isSameDay(date, end);
    });
  };

  const getRentalsForVehicle = (vehicleId) => {
    return rentals.filter(rental => 
      rental.vehicleId === vehicleId && 
      rental.status !== 'completed' && 
      rental.status !== 'cancelled'
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'error';
      case 'scheduled':
        return 'warning';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'default';
      default:
        return 'primary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Attivo';
      case 'scheduled':
        return 'Programmato';
      case 'completed':
        return 'Completato';
      case 'cancelled':
        return 'Annullato';
      default:
        return status;
    }
  };

  const getDaysInMonth = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Lunedì
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth() && 
           date.getFullYear() === currentDate.getFullYear();
  };

  const isToday = (date) => {
    return isSameDay(date, new Date());
  };

  const handlePreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const days = getDaysInMonth();
  const weekDays = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header con filtri */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={handlePreviousMonth} color="primary">
                <ChevronLeft />
              </IconButton>
              <Typography variant="h5" fontWeight={600}>
                {format(currentDate, 'MMMM yyyy', { locale: it })}
              </Typography>
              <IconButton onClick={handleNextMonth} color="primary">
                <ChevronRight />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Filtra per Veicolo</InputLabel>
              <Select
                value={selectedVehicle}
                label="Filtra per Veicolo"
                onChange={(e) => setSelectedVehicle(e.target.value)}
              >
                <MenuItem value="all">Tutti i Veicoli</MenuItem>
                {vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.name} - {vehicle.type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Calendario: 7 colonne fisse, una riga per settimana */}
      <Paper sx={{ p: 2 }}>
        {/* Intestazione giorni settimana - 7 colonne */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.5,
            mb: 1
          }}
        >
          {weekDays.map((day) => (
            <Box key={day} sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Righe = settimane, ogni riga ha 7 celle */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {weeks.map((week, weekIndex) => (
            <Box
              key={weekIndex}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.5,
                minHeight: 100
              }}
            >
              {week.map((day, dayIndex) => {
                const dayRentals = getRentalsForDate(day);
                const isCurrentMonthDay = isCurrentMonth(day);
                const isTodayDay = isToday(day);

                return (
                  <Card
                    key={dayIndex}
                    variant="outlined"
                    sx={{
                      minHeight: 100,
                      p: 1,
                      bgcolor: isCurrentMonthDay ? 'background.paper' : 'grey.50',
                      border: isTodayDay ? 2 : 1,
                      borderColor: isTodayDay ? 'primary.main' : 'divider',
                      opacity: isCurrentMonthDay ? 1 : 0.5,
                      '&:hover': {
                        boxShadow: 2,
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight={isTodayDay ? 700 : 500}
                        color={isTodayDay ? 'primary.main' : 'text.primary'}
                      >
                        {format(day, 'd')}
                      </Typography>
                      {dayRentals.length > 0 && (() => {
                        const groupCount = new Set(dayRentals.map(r => r.rentalGroupId ?? r.id)).size;
                        return (
                          <Chip
                            size="small"
                            label={groupCount}
                            color="primary"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        );
                      })()}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 72, overflow: 'auto' }}>
                      {(() => {
                        const byGroup = new Map();
                        dayRentals.forEach(r => {
                          const gid = r.rentalGroupId ?? `s-${r.id}`;
                          if (!byGroup.has(gid)) byGroup.set(gid, []);
                          byGroup.get(gid).push(r);
                        });
                        const groups = Array.from(byGroup.values()).slice(0, 3);
                        return groups.map((rentalsInGroup) => {
                          const r0 = rentalsInGroup[0];
                          const vehicleNames = rentalsInGroup.map(r => {
                            const v = vehicles.find(vv => vv.id === r.vehicleId);
                            return v ? v.name : 'N/A';
                          }).join(', ');
                          const customer = r0.customer || r0.Customer;
                          const isStart = isSameDay(new Date(r0.startDate), day);
                          const isEnd = isSameDay(new Date(r0.endDate), day);
                          const label = rentalsInGroup.length > 1
                            ? `${rentalsInGroup.length} veicoli`
                            : vehicleNames;
                          return (
                            <Tooltip
                              key={r0.rentalGroupId || r0.id}
                              title={
                                <Box>
                                  <Typography variant="caption" display="block">
                                    <strong>Cliente:</strong> {customer ? `${customer.firstName} ${customer.lastName}` : 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    <strong>Veicoli:</strong> {vehicleNames}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    <strong>Periodo:</strong> {format(new Date(r0.startDate), 'dd/MM/yyyy')} - {format(new Date(r0.endDate), 'dd/MM/yyyy')}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    <strong>Stato:</strong> {getStatusLabel(r0.status)}
                                  </Typography>
                                </Box>
                              }
                              arrow
                            >
                              <Chip
                                size="small"
                                label={`${label}${isStart ? ' ▶' : ''}${isEnd ? ' ◀' : ''}`}
                                color={getStatusColor(r0.status)}
                                sx={{
                                  fontSize: '0.65rem',
                                  height: 18,
                                  '& .MuiChip-label': { px: 0.5 }
                                }}
                              />
                            </Tooltip>
                          );
                        });
                      })()}
                      {dayRentals.length > 0 && (() => {
                        const byGroup = new Map();
                        dayRentals.forEach(r => {
                          const gid = r.rentalGroupId ?? `s-${r.id}`;
                          if (!byGroup.has(gid)) byGroup.set(gid, []);
                          byGroup.get(gid).push(r);
                        });
                        const totalGroups = byGroup.size;
                        return totalGroups > 3 ? (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            +{totalGroups - 3} altre prenotazioni
                          </Typography>
                        ) : null;
                      })()}
                    </Box>
                  </Card>
                );
              })}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Legenda */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Legenda
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
          <Chip size="small" label="Attivo" color="error" />
          <Chip size="small" label="Programmato" color="warning" />
          <Chip size="small" label="Completato" color="success" />
          <Chip size="small" label="Annullato" color="default" />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DirectionsBike fontSize="small" /> ▶ Inizio noleggio
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DirectionsBike fontSize="small" /> ◀ Fine noleggio
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default RentalCalendar;
